// SIWS CUSTOM: Duck Camo Hats picker — Phase 2 = swatch + hero swap + qty picker + ATC.
// Phase 3 will add tier progress bar UX (clones Pin-Up tee callout).
//
// Class instantiated once per .siws-duck-camo-hats__container element on the page.
// State held on `this`. ATC payload uses one `items[]` entry per ordered color
// (Pin-Up tee pattern), since Duck Camo has 4 actual Shopify variants — NOT
// Card Holder's single-item-with-color-properties pattern.

(function () {
  'use strict';

  // SIWS CUSTOM: per-variant snap unit (was 12). Mix-and-match across colors;
  // aggregate cart minimum (12) is enforced cart-side in cart.js.
  const SIWS_DUCK_CAMO_STEP    = 3;
  const SIWS_DUCK_CAMO_MIN     = 3;
  const SIWS_DUCK_CAMO_AGG_MIN = 12;  // PDP-side hint copy + tier preview floor

  // SIWS CUSTOM: Phase 3 — tier ladder. Total qty across all 4 colorways drives the math.
  // Sorted descending by min — _getCurrentTier iterates top-down and returns first match.
  // PDP-side preview only; actual discount applies via 3 admin Amount-off-product records.
  const SIWS_DUCK_CAMO_TIERS = [
    { min: 72, perEach: 20.00, savings: 2.00, max: true },
    { min: 48, perEach: 20.50, savings: 1.50 },
    { min: 36, perEach: 21.00, savings: 1.00 },
    { min: 12, perEach: 22.00, savings: 0    },
    { min: 0,  perEach: 22.00, savings: 0    },
  ];

  // SIWS CUSTOM: Phase 3 — drop trailing .00 on whole-dollar amounts; comma-separate >= 1000.
  // $36 (not $36.00), $20.50 stays, $3,432 / $3,432.50.
  function siwsDuckCamoFmtDollars(amount) {
    const opts = amount % 1 === 0
      ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return '$' + amount.toLocaleString('en-US', opts);
  }

  class SIWSDuckCamoHatsPicker {
    constructor(el) {
      this.el = el;
      this.productId = el.getAttribute('data-product-id');   // scopes cart tier math to THIS product
      this.colorQtys = {};                                   // { variantId: qty }
      this.cartCamoQty = 0;                                  // existing in-cart camo (cross-page-load)
      this.pickerQty = 0;                                    // sum of colorQtys (about to add)
      this.totalQty  = 0;                                    // pickerQty + cartCamoQty (drives tier math)
      this._init();
    }

    _init() {
      this._qtyGrid   = this.el.querySelector('#siws-duck-camo-hats-variant-grid');
      this._summaryEl = this.el.querySelector('#siws-duck-camo-hats-summary');
      this._atcBtn    = this.el.querySelector('#siws-duck-camo-hats-atc-btn');
      this._msgEl     = this.el.querySelector('#siws-duck-camo-hats-message');

      this._bindSwatches();
      this._bindQtyPicker();
      this._bindAtc();
      this._update();

      // SIWS CUSTOM (fixed 2026-09-16): cart-aware tier preview, scoped to THIS product.
      // Was matching on title text ('Duck Camo' + 'Hat'), which pooled the 5-Panel and
      // 7-Panel products together — but their discounts are scoped per product, so a cart
      // of 20+20 displayed "$21/Each Unlocked - 40 Hats" and was charged 40 x $22.
      // Matching on product_id makes the preview agree with what the discount records do.
      // Original note: fetch existing camo qty so a dealer
      // viewing Bark sees the right tier if Harvest is already in cart. Mirrors
      // tee-size-matrix.js Phase 4 pattern (defensive subscribe to cartUpdate).
      this._fetchCartCamoQty();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => this._fetchCartCamoQty());
      }
    }

    _fetchCartCamoQty() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.ok ? r.json() : { items: [] })
        .then((cart) => {
          this.cartCamoQty = (cart.items || [])
            .filter((i) => String(i.product_id) === String(this.productId))
            .reduce((s, i) => s + i.quantity, 0);
          this._update();
        })
        .catch(() => { /* graceful degrade — keep cartCamoQty at last known value */ });
    }

    // ── Swatches (Phase 1 logic preserved) ────────────────────
    _bindSwatches() {
      const swatchGroup = this.el.querySelector('#siws-duck-camo-hats-variant-grid');
      const heroEl      = this.el.querySelector('#siws-duck-camo-hats-hero');
      const colorNameEl = this.el.querySelector('#siws-duck-camo-hats-color-name');
      if (!swatchGroup || !heroEl || !colorNameEl) return;

      swatchGroup.addEventListener('click', (e) => {
        const swatchBtn = e.target.closest('.siws-duck-camo-hats__swatch');
        if (!swatchBtn) return;
        if (swatchBtn.classList.contains('siws-duck-camo-hats__swatch--active')) return;

        swatchGroup.querySelectorAll('.siws-duck-camo-hats__swatch').forEach((b) => {
          b.classList.remove('siws-duck-camo-hats__swatch--active');
          b.setAttribute('aria-pressed', 'false');
        });
        swatchBtn.classList.add('siws-duck-camo-hats__swatch--active');
        swatchBtn.setAttribute('aria-pressed', 'true');

        const colorName = swatchBtn.dataset.colorName || '';
        colorNameEl.textContent = colorName;

        const newSrc = swatchBtn.dataset.imageUrl;
        if (newSrc && heroEl.getAttribute('src') !== newSrc) {
          heroEl.classList.add('is-swapping');
          const tmp = new Image();
          tmp.onload = () => {
            heroEl.setAttribute('src', newSrc);
            heroEl.setAttribute('alt', colorName);
            requestAnimationFrame(() => heroEl.classList.remove('is-swapping'));
          };
          tmp.onerror = () => heroEl.classList.remove('is-swapping');
          tmp.src = newSrc;
        }
      });
    }

    // ── Qty picker grid (4 rows, one per variant) ─────────────
    _bindQtyPicker() {
      if (!this._qtyGrid) return;

      // Stepper +/− clicks
      this._qtyGrid.addEventListener('click', (e) => {
        const minusBtn = e.target.closest('.siws-duck-camo-hats__qty-minus');
        const plusBtn  = e.target.closest('.siws-duck-camo-hats__qty-plus');
        if (minusBtn) this._stepQty(minusBtn.dataset.variantId, -SIWS_DUCK_CAMO_STEP);
        if (plusBtn)  this._stepQty(plusBtn.dataset.variantId, +SIWS_DUCK_CAMO_STEP);
      });

      // Input event — track raw during typing so live total reacts; snap on blur
      this._qtyGrid.addEventListener('input', (e) => {
        const inp = e.target.closest('.siws-duck-camo-hats__qty-input');
        if (!inp) return;
        const raw = parseInt(inp.value, 10);
        this.colorQtys[inp.dataset.variantId] = (raw > 0) ? raw : 0;
        this._update();
      });

      // Blur snap — capture phase since blur doesn't bubble
      this._qtyGrid.addEventListener('blur', (e) => {
        const inp = e.target.closest('.siws-duck-camo-hats__qty-input');
        if (!inp) return;
        this._validateAndSnap(inp);
      }, true);
    }

    _stepQty(variantId, delta) {
      const current = this.colorQtys[variantId] || 0;
      const next    = Math.max(0, current + delta);
      this.colorQtys[variantId] = next;
      const inp = this._qtyGrid.querySelector(
        '.siws-duck-camo-hats__qty-input[data-variant-id="' + variantId + '"]'
      );
      if (inp) {
        inp.value       = next > 0 ? String(next) : '';
        inp.placeholder = '0';
      }
      this._clearMessage();
      this._update();
    }

    // SIWS CUSTOM: Snap UP (Math.ceil), not nearest (Math.round). 5 → 12, 13 → 24, 30 → 36.
    // Differs from state-hats-picker.js which uses Math.round; aligns with Phase 2 spec.
    _validateAndSnap(inp) {
      const variantId = inp.dataset.variantId;
      const raw = parseInt(inp.value, 10);

      if (isNaN(raw) || raw <= 0) {
        this.colorQtys[variantId] = 0;
        inp.value       = '';
        inp.placeholder = '0';
        this._update();
        return;
      }

      const snapped = Math.max(
        SIWS_DUCK_CAMO_MIN,
        Math.ceil(raw / SIWS_DUCK_CAMO_STEP) * SIWS_DUCK_CAMO_STEP
      );

      if (snapped !== raw) {
        const variantTitle = inp.dataset.variantTitle || 'this color';
        this._showMessage(
          'Quantities must be in multiples of ' + SIWS_DUCK_CAMO_STEP +
          '. ' + variantTitle + ' rounded up to ' + snapped + '.'
        );
      } else {
        this._clearMessage();
      }

      inp.value = String(snapped);
      this.colorQtys[variantId] = snapped;
      this._update();
    }

    // ── ATC ──────────────────────────────────────────────────
    _bindAtc() {
      if (!this._atcBtn) return;
      this._atcBtn.addEventListener('click', () => this._addToCart());
    }

    // SIWS CUSTOM: Defensive snap-on-ATC — if user clicks ATC without blurring an
    // invalid input, snap all inputs first so the constraint holds at submit.
    _snapAllInputs() {
      this._qtyGrid.querySelectorAll('.siws-duck-camo-hats__qty-input').forEach((inp) => {
        this._validateAndSnap(inp);
      });
    }

    _addToCart() {
      if (!this._atcBtn || this._atcBtn.disabled) return;

      this._snapAllInputs();
      if (this.totalQty === 0) return;

      // SIWS CUSTOM: Multi-variant items[] payload — one cart line per ordered color.
      // No `properties` — Dawn surfaces variant title natively as the cart line label.
      const items = [];
      Object.keys(this.colorQtys).forEach((vid) => {
        const qty = this.colorQtys[vid];
        if (qty <= 0) return;
        items.push({
          id:       parseInt(vid, 10),
          quantity: qty,
        });
      });

      if (items.length === 0) return;

      const originalText = this._atcBtn.textContent;
      this._atcBtn.disabled    = true;
      this._atcBtn.textContent = 'Adding…';
      this._clearMessage();

      // SIWS CUSTOM (2026-05-12): augment body with Section Rendering API hints
      // so the Dawn cart drawer/notification can re-render itself from the response.
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = {
        items: items,
        sections:     cart ? cart.getSectionsToRender().map((s) => s.id) : ['cart-icon-bubble'],
        sections_url: window.location.pathname,
      };

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      })
        .then(async (res) => {
          if (!res.ok) throw new Error('Cart add failed');
          // SIWS CUSTOM (2026-05-12): swap redirect for Dawn cart drawer/notification.
          const responseData = await res.json();
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'siws-duck-camo-hats', cartData: responseData });
          }
          if (cart) {
            cart.renderContents(responseData);
          } else {
            // SIWS CUSTOM (2026-05-12): Page-mode — stay on PDP, refresh cart bubble.
            const newBubbleHTML = responseData.sections && responseData.sections['cart-icon-bubble'];
            const liveBubble = document.getElementById('cart-icon-bubble');
            if (newBubbleHTML && liveBubble) {
              const parsed = new DOMParser().parseFromString(newBubbleHTML, 'text/html');
              const inner = parsed.querySelector('.shopify-section');
              if (inner) liveBubble.innerHTML = inner.innerHTML;
            }
            // SIWS CUSTOM (2026-05-12): clear picker state so a re-click can't double-add.
            // _update() now sets textContent via _validationMessage(), so no manual restore needed —
            // empty picker → "Add 12 units across colors to add to cart".
            this.colorQtys = {};
            this._qtyGrid.querySelectorAll('.siws-duck-camo-hats__qty-input').forEach((inp) => { inp.value = ''; });
            this._update();
          }
        })
        .catch((err) => {
          console.error('SIWS: Duck Camo Hats add-to-cart failed', err);
          this._atcBtn.disabled    = false;
          this._atcBtn.textContent = originalText;
          this._showMessage('Something went wrong — please try again.');
        });
    }

    // SIWS CUSTOM (2026-05-12): Cart-aware cascade — returns the user-facing
    // reason the ATC button is blocked, or '' if valid. Aggregate min is checked
    // against pickerQty + cartCamoQty so dealers who already met the min in cart
    // can submit additional small qtys. Per-color rules apply to picker entries
    // only — values already in cart are settled.
    //   1. Picker=0, Cart=0           → "Add 12 units across colors to add to cart"
    //   2. Picker=0, Cart>=12         → "Select hats to add"
    //   3. Picker=0, Cart<12          → "Add N more units (Y of 12)" (cart-only progress)
    //   4. Picker>0, Total<12         → "Add N more units (Y of 12)" (combined progress)
    //   5. Color in [1,2]             → "{Color} needs minimum 3 (or 0)"
    //   6. Color not %3               → "{Color} must be in multiples of 3"
    _validationMessage() {
      const pickerOnly = this.pickerQty;
      const cartOnly = this.cartCamoQty;
      const total = pickerOnly + cartOnly;

      // Fresh-state friendly message — nothing in cart, nothing in picker.
      if (pickerOnly === 0 && cartOnly === 0) {
        return 'Add ' + SIWS_DUCK_CAMO_AGG_MIN + ' units across colors to add to cart';
      }

      // Picker empty, cart already at or above min — nothing to submit.
      if (pickerOnly === 0 && cartOnly >= SIWS_DUCK_CAMO_AGG_MIN) {
        return 'Select hats to add';
      }

      // Picker empty, cart still below min — guide dealer back to picker.
      if (pickerOnly === 0) {
        const need = SIWS_DUCK_CAMO_AGG_MIN - cartOnly;
        return 'Add ' + need + ' more units (' + cartOnly + ' of ' + SIWS_DUCK_CAMO_AGG_MIN + ')';
      }

      // Picker has entries — check combined total against aggregate min.
      if (total < SIWS_DUCK_CAMO_AGG_MIN) {
        const need = SIWS_DUCK_CAMO_AGG_MIN - total;
        return 'Add ' + need + ' more units (' + total + ' of ' + SIWS_DUCK_CAMO_AGG_MIN + ')';
      }

      // Per-color rules apply to PICKER entries only — cart values are settled.
      const entries = Object.entries(this.colorQtys);
      for (let i = 0; i < entries.length; i++) {
        const vid = entries[i][0];
        const qty = entries[i][1];
        if (qty > 0 && qty < SIWS_DUCK_CAMO_MIN) {
          return this._variantName(vid) + ' needs minimum ' + SIWS_DUCK_CAMO_MIN + ' (or 0)';
        }
      }
      for (let i = 0; i < entries.length; i++) {
        const vid = entries[i][0];
        const qty = entries[i][1];
        if (qty > 0 && qty % SIWS_DUCK_CAMO_STEP !== 0) {
          return this._variantName(vid) + ' must be in multiples of ' + SIWS_DUCK_CAMO_STEP;
        }
      }

      return '';
    }

    // SIWS CUSTOM (2026-05-12): Resolves variantId → human-readable color name
    // from the qty-input's data-variant-title attribute. Falls back to "Color".
    _variantName(variantId) {
      const inp = this._qtyGrid && this._qtyGrid.querySelector(
        '.siws-duck-camo-hats__qty-input[data-variant-id="' + variantId + '"]'
      );
      return (inp && inp.dataset.variantTitle) || 'Color';
    }

    // ── State updates ────────────────────────────────────────
    _update() {
      this.pickerQty = Object.values(this.colorQtys).reduce((s, q) => s + q, 0);
      // SIWS CUSTOM: totalQty is now combined (cart + picker), drives tier math.
      this.totalQty  = this.pickerQty + this.cartCamoQty;
      if (this._atcBtn) {
        // SIWS CUSTOM (2026-05-12): ATC gating mirrors screwdriver-picker.js —
        // _validationMessage() returns '' when all rules pass; otherwise its
        // string is rendered as the disabled-button label.
        const blocked = this._validationMessage();
        this._atcBtn.disabled    = blocked !== '';
        this._atcBtn.textContent = blocked || 'Add to Cart';
      }
      this._renderSummary();
      this._renderTierCallout();
    }

    // SIWS CUSTOM: Phase 2.3 — per-color summary box (Card Holder pattern).
    // One row per variant where qty > 0, in DOM order. Per-each price is
    // tier-aware (current tier's perEach), so rows update across tier transitions.
    // Hidden below 12 — the "Add X More" hint covers that state.
    _renderSummary() {
      if (!this._summaryEl) return;

      // SIWS CUSTOM: hide when picker is empty (was: combined < 12). Summary shows
      // what's about to be added; cart-only state belongs to the tier callout.
      if (this.pickerQty === 0) {
        this._summaryEl.hidden = true;
        this._summaryEl.innerHTML = '';
        return;
      }

      // perEach reads combined-tier (cart + picker) so a dealer adding Bark while
      // Harvest is in cart at qty 12 sees the unlocked $22 tier reflected here.
      const perEach = this._getCurrentTier().perEach;

      const rowsHtml = [];
      this._qtyGrid.querySelectorAll('.siws-duck-camo-hats__variant-card').forEach((card) => {
        const vid = card.dataset.variantId;
        const qty = this.colorQtys[vid] || 0;
        if (qty <= 0) return;
        const labelEl = card.querySelector('.siws-duck-camo-hats__variant-label');
        const title = labelEl ? labelEl.textContent : 'Color';
        const lineCost = qty * perEach;
        rowsHtml.push(
          '<tr>' +
            '<td>' + title + '</td>' +
            '<td class="siws-duck-camo-hats__summary-qty-cell">' +
              qty + ' &times; ' + siwsDuckCamoFmtDollars(perEach) +
              ' = ' + siwsDuckCamoFmtDollars(lineCost) +
            '</td>' +
          '</tr>'
        );
      });

      // SIWS CUSTOM: cost row uses pickerQty (what's being added), not combined.
      const totalCost = this.pickerQty * perEach;
      const unitLabel = this.pickerQty === 1 ? 'Hat' : 'Hats';

      this._summaryEl.innerHTML =
        '<h4 class="siws-duck-camo-hats__summary-title">Adding to Cart</h4>' +
        '<table class="siws-duck-camo-hats__summary-table">' +
          '<tbody>' + rowsHtml.join('') + '</tbody>' +
          '<tfoot>' +
            '<tr class="siws-duck-camo-hats__summary-total-row">' +
              '<td>Total</td>' +
              '<td class="siws-duck-camo-hats__summary-qty-cell">' +
                this.totalQty + ' ' + unitLabel + ' &middot; ' + siwsDuckCamoFmtDollars(totalCost) +
              '</td>' +
            '</tr>' +
          '</tfoot>' +
        '</table>';
      this._summaryEl.hidden = false;
    }

    // SIWS CUSTOM: Phase 3 — find first tier where totalQty >= min (top-down).
    _getCurrentTier() {
      for (let i = 0; i < SIWS_DUCK_CAMO_TIERS.length; i++) {
        if (this.totalQty >= SIWS_DUCK_CAMO_TIERS[i].min) {
          return SIWS_DUCK_CAMO_TIERS[i];
        }
      }
      return SIWS_DUCK_CAMO_TIERS[SIWS_DUCK_CAMO_TIERS.length - 1];
    }

    // SIWS CUSTOM: Phase 3 — find lowest min strictly > totalQty, or null at top tier.
    _getNextTier() {
      const upcoming = SIWS_DUCK_CAMO_TIERS
        .filter((t) => t.min > this.totalQty)
        .sort((a, b) => a.min - b.min);
      return upcoming[0] || null;
    }

    // SIWS CUSTOM: Phase 2.2 (revised) — Pin-Up tee parity.
    // Two callout styles total: default (green) + --max (gold). Below-min renders
    // a plain hint (no callout box) — matches Pin-Up's empty-state hint pattern.
    // Copy stays dollar-anchored per Cass's email language; max-tier secondary uses
    // "Saving $X On This Order" parallel to Pin-Up's "Saved $X on this order".
    _renderTierCallout() {
      const calloutEl = this.el.querySelector('#siws-duck-camo-hats-tier-callout');
      if (!calloutEl) return;

      const total = this.totalQty;

      // Below combined 12 — plain hint, no callout box (Pin-Up parity).
      // SIWS CUSTOM: now uses AGG_MIN (cart enforcement floor), not per-variant MIN.
      // total is combined (cart + picker), so the hint reflects "across colors" math.
      if (total < SIWS_DUCK_CAMO_AGG_MIN) {
        const remaining = SIWS_DUCK_CAMO_AGG_MIN - total;
        calloutEl.innerHTML =
          '<p class="siws-duck-camo-hats__hint">Add ' + remaining + ' More Across Colors To Checkout</p>';
        return;
      }

      const current    = this._getCurrentTier();
      const next       = this._getNextTier();
      const savingsNow = total * current.savings;

      // Max tier (72+) — gold callout, no progress bar
      if (!next) {
        calloutEl.innerHTML =
          '<div class="siws-duck-camo-hats__callout siws-duck-camo-hats__callout--max">' +
            '<span class="siws-duck-camo-hats__callout-icon">🎉</span>' +
            '<div class="siws-duck-camo-hats__callout-text">' +
              '<p class="siws-duck-camo-hats__callout-primary">Best Price Unlocked: ' +
                siwsDuckCamoFmtDollars(current.perEach) + '/Each</p>' +
              '<p class="siws-duck-camo-hats__callout-secondary">Saving ' +
                siwsDuckCamoFmtDollars(savingsNow) + ' On This Order</p>' +
            '</div>' +
          '</div>';
        return;
      }

      // Active tier (12-71) — green callout with progress bar.
      // "Saving $X" suffix only when current tier has savings > 0 ($22 floor stays clean).
      // SIWS CUSTOM: Phase 2.5 — bar is cumulative-to-max, not tier-relative. Tier crossings
      // become language events ("$X/Each Unlocked") instead of visual cliffs. Label denominator
      // matches the bar (both reference the top of the ladder).
      const remaining   = next.min - total;
      const maxTierMin  = SIWS_DUCK_CAMO_TIERS[0].min;
      const progressPct = Math.min((total / maxTierMin) * 100, 100);

      const primaryText = current.savings > 0
        ? siwsDuckCamoFmtDollars(current.perEach) + '/Each Unlocked — ' + total + ' Hats — Saving ' +
          siwsDuckCamoFmtDollars(savingsNow) + ' On This Order'
        : siwsDuckCamoFmtDollars(current.perEach) + '/Each — ' + total + ' Hat' +
          (total === 1 ? '' : 's');

      const secondaryText =
        'Add ' + remaining + ' More For ' + siwsDuckCamoFmtDollars(next.perEach) + '/Each';

      calloutEl.innerHTML =
        '<div class="siws-duck-camo-hats__callout">' +
          '<span class="siws-duck-camo-hats__callout-icon">↑</span>' +
          '<div class="siws-duck-camo-hats__callout-text">' +
            '<p class="siws-duck-camo-hats__callout-primary">' + primaryText + '</p>' +
            '<p class="siws-duck-camo-hats__callout-secondary">' + secondaryText + '</p>' +
            '<div class="siws-duck-camo-hats__callout-progress" ' +
                 'style="display:block;width:100%;height:14px;' +
                        'background:#d1d5db;border-radius:7px;' +
                        'overflow:hidden;margin-top:8px;">' +
              '<div class="siws-duck-camo-hats__callout-progress-bar" ' +
                   'style="display:block;height:14px;' +
                          'background:#16a34a;' +
                          'width:' + progressPct.toFixed(1) + '%;"></div>' +
            '</div>' +
            '<p class="siws-duck-camo-hats__callout-progress-label">' + total + ' / ' + maxTierMin + '</p>' +
          '</div>' +
        '</div>';
    }

    // ── Inline messages ──────────────────────────────────────
    _showMessage(msg) {
      if (!this._msgEl) return;
      this._msgEl.textContent   = msg;
      this._msgEl.style.display = 'block';
    }
    _clearMessage() {
      if (!this._msgEl) return;
      this._msgEl.style.display = 'none';
    }
  }

  function siwsDuckCamoInit() {
    document.querySelectorAll('.siws-duck-camo-hats__container').forEach((el) => {
      new SIWSDuckCamoHatsPicker(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', siwsDuckCamoInit);
  } else {
    siwsDuckCamoInit();
  }
})();
